/* eslint-disable no-await-in-loop, no-loop-func */

/**
 * Default options for batch processing.
 * @param {Number} batchSize - The number of items to batch together.
 * @param {Number} batchDelay - The delay (in milliseconds) between batches.
 * @param {Number} maxRetries - Maximum number of times to retry.
 */
const DEFAULT_OPTIONS = { batchSize: 10, batchDelay: 500, maxRetries: 3 };

/**
 * Maximum delay (in milliseconds) for the exponential backoff strategy.
 */
const MAX_BACKOFF_DELAY = 60000; // e.g., 60 seconds

/**
 * The acceptable percentage of failures in a batch.
 * If the failure rate exceeds this percentage after processing,
 * the function will throw an error.
 */
const ACCEPTABLE_FAILURE_RATE = 5; // e.g., 5%

/**
 * Data to be passed to the callback function.
 * @param {String} title - The title of the batch process.
 * @param {Number} index - The index of the current item being processed.
 * @param {Number} total - The total number of items to be processed.
 * @param {Number} percent - The percentage of items processed.
 */
export class SwalData {
    constructor(title = '', index = 0, total = 0, percent = null) {
        this.title = title;
        this.index = index;
        this.total = total;

        // Use provided percent or calculate it based on index and total if they're available
        if (percent !== null) {
            this.percent = percent;
        }
        else if (index && total) {
            this.percent = ((index / total) * 100).toFixed(2);
        }
        else {
            this.percent = 0;
        }
    }
}

/**
 * Processes items in batches.
 *
 * @param {Array} items - List of items to be processed.
 * @param {Function} callback - The function to process each item.
 * @param {Object} swal - Object containing information about the progress and callback function for SWAL.
 * @param {Object} options - Configuration options including batchSize, and batchDelay.
 */
export async function batchProcess(items, callback, swal, options = { }) {
    const config = { ...DEFAULT_OPTIONS, ...options };
    let failureCount = 0;

    for (let i = 0; i < items.length; i += config.batchSize) {
        console.debug(`\tBatch ${i} to ${i + config.batchSize} of ${items.length}`)
        const batch = items.slice(i, i + config.batchSize);
        await Promise.all(batch.map(async (item, index) => {
            try {
                await callback(item, swal);
            }
            catch (error) {
                failureCount++;
                console.error('Batch Error', index, error);
            }
        }));

        // Report the progress to SWAL
        for (let j = 0; j < batch.length; j++) {
            if (swal && swal.callback) {
                const itemsProcessed = i + j + 1; // Total number of items processed up to the current item
                const percent = (itemsProcessed / items.length) * 100;

                swal.callback({
                    title: swal.title,
                    index: itemsProcessed,
                    total: items.length,
                    percent: percent.toFixed(2),
                });
            }
            await new Promise(resolve => setTimeout(resolve, 50)); // Short delay to allow UI to update
        }

        if (i + config.batchSize < items.length) {
            await new Promise((resolve) => setTimeout(resolve, config.batchDelay));
        }
    }
    // Check failure rate after processing all items
    const failureRate = (failureCount / items.length) * 100;
    if (failureRate > ACCEPTABLE_FAILURE_RATE) {
        throw new Error(`Batch processing had a ${failureRate.toFixed(2)}% failure rate which exceeds the acceptable limit of ${ACCEPTABLE_FAILURE_RATE}%`);
    }
}

/**
 * Processes items in batches with retries upon failure.
 * Implements an exponential backoff strategy.
 *
 * @param {Array} items - List of items to be processed.
 * @param {Function} callback - The function to process each item.
 * @param {Object} swal - Object containing information about the progress and callback function for SWAL.
 * @param {Object} options - Configuration options including batchSize, batchDelay, and maxRetries.
 */
export async function batchProcessWithRetry(items, callback, swal, options = {}) {
    const config = { ...DEFAULT_OPTIONS, ...options };
    const errors = [];
    let failureCount = 0;

    for (let i = 0; i < items.length; i += config.batchSize) {
        //console.debug(`\tBatch ${i} to ${i + config.batchSize} of ${items.length}`)
        const batch = items.slice(i, i + config.batchSize);
        await Promise.all(batch.map(async (item) => {
            let retries = 0;
            let backoffDelay = 1000; // Initial backoff delay in milliseconds
            while (retries < config.maxRetries) {
                try {
                    await callback(item, swal);
                    break;
                }
                catch (error) {
                    errors.push(error);
                    // Retry the operation
                    retries++;
                    //console.debug(`\t\tRetrying item ${index} (attempt ${retries})`, error.message);
                    await new Promise((resolve) => setTimeout(resolve, backoffDelay));
                    backoffDelay = Math.min(backoffDelay * 2, MAX_BACKOFF_DELAY);
                }
            }

            // If retries are exhausted and still failed, log and increment failureCount
            if (retries >= config.maxRetries) {
                //console.error(`Failed to process item ${index} after ${config.maxRetries} retries`);
                failureCount++;
            }
        }));

        // Report the progress to SWAL
        for (let j = 0; j < batch.length; j++) {
            if (swal && swal.callback) {
                const itemsProcessed = i + j + 1; // Total number of items processed up to the current item
                const percent = (itemsProcessed / items.length) * 100;

                swal.callback({
                    title: swal.title,
                    index: itemsProcessed,
                    total: items.length,
                    percent: percent.toFixed(2),
                });
            }
            await new Promise(resolve => setTimeout(resolve, 50)); // Short delay to allow UI to update
        }

        if (i + config.batchSize < items.length) {
            await new Promise((resolve) => setTimeout(resolve, config.batchDelay));
        }
    }

    // Check failure rate after processing all items
    const failureRate = (failureCount / items.length) * 100;
    if (failureRate > ACCEPTABLE_FAILURE_RATE) {
        console.error('Batch Errors', errors);
        throw new Error(`Batch processing had a ${failureRate.toFixed(2)}% failure rate which exceeds the acceptable limit of ${ACCEPTABLE_FAILURE_RATE}%`);
    }
}
